<template>
  <div
    class="tw-flex tw-flex-col tw-gap-2 tw-p-4 tw-bg-true-gray-50"
    :data-test="`expense-report-${expenseReport.localId}`"
  >
    <div class="tw-flex tw-justify-between tw-w-full">
      <div class="tw-w-full">
        <div class="tw-flex tw-gap-sm">
          <div class="tw-mt-0.5">
            <WdsIcon name="report-data" :size="16" />
          </div>
          <WdsText class="tw-flex-1">
            <template #default>
              <div
                class="tw-max-w-md tw-overflow-hidden tw-truncate tw-whitespace-nowrap tw-text-ellipsis"
                :title="expenseReport.title"
              >
                <span class="tw-text-base">{{ expenseReport.title }}</span>
              </div>
            </template>
            <template v-if="expenseReport.description" #subText>
              <div
                class="tw-max-w-lg tw-overflow-hidden tw-truncate tw-whitespace-nowrap tw-text-ellipsis"
                :title="expenseReport.description"
              >
                <span class="tw-text-sm tw-font-medium">
                  {{ expenseReport.description }}
                </span>
              </div>
            </template>
          </WdsText>
        </div>
      </div>
      <div v-if="!disabled" class="tw-flex tw-gap-xs">
        <ModalExpenseReport
          v-slot="{ triggerModal }"
          :expense-report="expenseReport"
          :expense-report-currency="paymentRequestStore.currency"
          :hire-id="paymentRequestStore.hireLocalId"
          @report-updated="handleUpdatedExpenseReport"
        >
          <WdsButtonGhost data-test="edit-expense-report-button" @click="triggerModal">
            <WdsIcon name="edit" :size="16" class="tw-text-gray-700" />
          </WdsButtonGhost>
        </ModalExpenseReport>

        <div>
          <WdsTooltip
            :text="
              $t(
                'Unlink the expense report from this payment request and keep it as a standalone report.',
              )
            "
            :left="true"
          >
            <WdsButtonGhost
              data-test="unlink-expense-report-button"
              @click="handleUnlinkExpenseReport"
            >
              <WdsIcon name="unlink" :size="16" class="tw-text-gray-700" />
            </WdsButtonGhost>
          </WdsTooltip>
        </div>
        <div>
          <WdsButtonGhost
            :data-test="`delete-expense-report-button`"
            @click="attemptExpenseReportDeletion(expenseReport)"
          >
            <WdsIcon name="trash-can" :size="16" class="tw-text-gray-700" />
          </WdsButtonGhost>
        </div>
      </div>
    </div>
    <WdsDivider />
    <div class="tw-overflow-x-auto tw-flex tw-flex-col tw-gap-2">
      <div
        v-for="expense in expenseReport.expenses"
        :key="expense.localId"
        class="tw-flex tw-flex-col tw-gap-2"
      >
        <div class="tw-grid tw-grid-cols-[minmax(100px,2fr)_minmax(160px,1fr)_minmax(290px,1fr)]">
          <WdsText>
            <template #default>
              <div
                class="tw-max-w-sm tw-overflow-hidden tw-truncate tw-whitespace-nowrap tw-text-ellipsis"
                :title="expense.vendor"
              >
                <span class="tw-text-base">{{ expense.vendor }}</span>
              </div>
            </template>
            <template v-if="expense.description" #subText>
              <div
                class="tw-max-w-md tw-overflow-hidden tw-truncate tw-whitespace-nowrap tw-text-ellipsis"
                :title="expense.description"
              >
                <span class="tw-text-sm tw-font-medium">
                  {{ expense.description }}
                </span>
              </div>
            </template>
          </WdsText>
          <div
            v-if="expense.customFieldValues?.length"
            class="tw-flex tw-flex-col tw-justify-center tw-gap-1"
          >
            <span
              v-for="customFieldValue in expense.customFieldValues"
              :key="customFieldValue.id"
              class="tw-text-sm tw-text-gray-700 tw-truncate"
              :title="customFieldValue.displayValue"
            >
              {{ customFieldValue.displayValue }}
            </span>
          </div>
          <div v-else />
          <div class="tw-grid tw-grid-cols-[minmax(90px,1fr)_minmax(200px,2fr)] tw-items-center">
            <span class="tw-text-gray-700 tw-whitespace-nowrap tw-flex tw-justify-end">
              {{ moment(expense.expenseAt, 'YYYY-MM-DD').format('YYYY-MM-DD') }}
            </span>
            <div class="tw-flex tw-justify-end tw-gap-lg">
              <span class="tw-whitespace-nowrap">
                {{ expense.currency }}
                {{ currencyJs(expense.amount / 100, paymentRequestStore.currencyConfig).format() }}
              </span>
              <div v-if="!disabled" class="tw-flex tw-gap-xs tw-justify-end">
                <ModalExpense
                  :view-only="disabled"
                  :expense-report-id="expenseReport.localId"
                  :expense-report-currency="expense.currency"
                  :is-open="expenseModalsOpenState[expense.localId]"
                  :expense="expense"
                  :files-required="true"
                  :should-exclude-vat="paymentRequestStore.hasFreelancerVat"
                  :account-id="paymentRequestStore.companyId"
                  :custom-field-provider-account-ids="paymentRequestStore.customFieldProviderAccountIds"
                  @expense-updated="handleUpdatedExpense"
                  @closed="closeExpenseModal(expense.localId)"
                />
                <div>
                  <WdsButtonGhost
                    :data-test="`edit-expense-item-button-${expense.localId}`"
                    @click="expenseModalsOpenState[expense.localId] = true"
                  >
                    <WdsIcon name="edit" :size="16" class="tw-text-gray-700" />
                  </WdsButtonGhost>
                </div>
                <div>
                  <WdsButtonGhost
                    :data-test="`delete-expense-button-${expense.localId}`"
                    @click="attemptExpenseDeletion(expense)"
                  >
                    <WdsIcon name="trash-can" :size="16" class="tw-text-gray-700" />
                  </WdsButtonGhost>
                </div>
              </div>
            </div>
          </div>
        </div>
        <WdsDivider />
      </div>
      <div class="tw-flex tw-justify-between tw-mt-2">
        <template v-if="!disabled">
          <ModalExpense
            :expense-report-id="expenseReport.localId"
            :expense-report-currency="paymentRequestStore.currency"
            :is-open="expenseModalsOpenState.newExpenseModal"
            :files-required="true"
            :should-exclude-vat="paymentRequestStore.hasFreelancerVat"
            :account-id="paymentRequestStore.companyId"
            :custom-field-provider-account-ids="paymentRequestStore.customFieldProviderAccountIds"
            @closed="closeExpenseModal('newExpenseModal')"
            @expense-created="handleCreatedExpense"
          />
          <WdsButton
            secondary
            class="tw-flex tw-items-center tw-gap-xs"
            sm
            data-test="add-expense-item-button"
            @click="expenseModalsOpenState.newExpenseModal = true"
          >
            <WdsIcon name="plus" :size="16" />
            {{ $t('Add item') }}
          </WdsButton>
        </template>
        <span v-else></span>
        <div class="tw-font-medium tw-flex tw-gap-md">
          {{ $t('Total') }}:
          <span>
            {{ paymentRequestStore.currency }}
            {{ currencyJs(expenseReport.total / 100, paymentRequestStore.currencyConfig).format() }}
          </span>
        </div>
      </div>
    </div>
  </div>

  <ModalDeleteExpenses
    :show="isDeleteModalOpen"
    :marked-for-deletion="markedForDeletion"
    @unlink="handleUnlinkExpenseReport"
    @deleted="handleDeletedExpense"
    @cancel="handleCloseDeletionModal"
  />
</template>

<script setup lang="ts">
import { ref, Ref, watchEffect } from 'vue'
import {
  WdsButton,
  WdsButtonGhost,
  WdsDivider,
  WdsIcon,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import moment from 'moment'
import currencyJs from 'currency.js'
import ModalExpense from '@/components/ModalExpense.vue'
import { usePaymentRequestStore } from '@/components/PaymentRequest/store/usePaymentRequestStore'
import { useExpensesStore } from '@/components/PaymentRequest/store/useExpensesStore'
import ModalExpenseReport from '@/components/ModalExpenseReport.vue'
import { useTranslation } from '@/composables/translation'
import ModalDeleteExpenses from '@/components/PaymentRequest/Expenses/ModalDeleteExpenses.vue'

const { $t } = useTranslation()

interface Props {
  expenseReport: any
  disabled: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
})
const emit = defineEmits([
  'expense-report-updated',
  'expense-report-unlinked',
  'expense-report-deleted',
  'expenses-updated',
])

const paymentRequestStore = usePaymentRequestStore()
const expensesStore = useExpensesStore()

const isDeleteModalOpen = ref(false)
const markedForDeletion: Ref<{
  localId: number
  deletableType: 'expense' | 'expense-report'
} | null> = ref(null)

const expenseModalsOpenState: Ref<Record<number, boolean> & { newExpenseModal: boolean }> = ref({
  newExpenseModal: false,
})

// watch for changes in the expenseReport.expenses array and update expenseModalsOpenState accordingly
watchEffect(() => {
  props.expenseReport.expenses?.forEach((expense: any) => {
    if (!expenseModalsOpenState.value[expense.localId]) {
      expenseModalsOpenState.value[expense.localId] = false
    }
  })
})

const handleUpdatedExpenseReport = (expenseReport: any) => {
  expensesStore.updateExpenseReport(expenseReport)
  emit('expense-report-updated')
}

const handleUnlinkExpenseReport = () => {
  expensesStore.unlinkExpenseReport(props.expenseReport.localId)
  emit('expense-report-unlinked')
  isDeleteModalOpen.value = false
  markedForDeletion.value = null
}

const handleUpdatedExpense = (expense: any) => {
  expensesStore.updateExpense(expense, props.expenseReport.localId)
  emit('expenses-updated')
}

const handleCreatedExpense = (expense: any) => {
  expensesStore.addExpense(expense, props.expenseReport.localId)
  emit('expenses-updated')
}

const attemptExpenseReportDeletion = (expenseReport: any) => {
  markedForDeletion.value = { localId: expenseReport.localId, deletableType: 'expense-report' }
  isDeleteModalOpen.value = true
}

const attemptExpenseDeletion = (expense: any) => {
  markedForDeletion.value = { localId: expense.localId, deletableType: 'expense' }
  isDeleteModalOpen.value = true
}

const handleCloseDeletionModal = () => {
  isDeleteModalOpen.value = false
  markedForDeletion.value = null
}

const handleDeletedExpense = () => {
  emit('expense-report-deleted')
  if (!markedForDeletion.value) return
  if (markedForDeletion.value.deletableType === 'expense-report') {
    expensesStore.deleteExpenseReport(markedForDeletion.value.localId)
  } else {
    expensesStore.deleteExpense(markedForDeletion.value.localId)
  }
  isDeleteModalOpen.value = false
  markedForDeletion.value = null
}
const closeExpenseModal = (expenseReportId: number | 'newExpenseModal') => {
  expenseModalsOpenState.value[expenseReportId] = false
}
</script>
